import { useRef } from 'react';

import { cn } from '@rocco/utils/cn';
import { useOpenBookMeetingGate } from '~/modules-rocco/booking/book-meeting-gate-state';
import { useBookMeetingGateEnabled } from '~/modules-rocco/booking/book-meeting-gate-state';
import {
    useEnquiryModal,
    type EnquiryModalProfessional,
} from '~/modules/enquiry/hooks/use-enquiry-modal';
import { DIRECTORY_GUTTER_X } from '~/modules/articles-revamp/component/EditorialArticleGrid';
import { detailCta, SectionHead } from '~/ui-rocco/pages/detail-shared';
import {
    ACTION_CARD_ACTION_CLASS,
    ACTION_CARD_CLASS,
    ACTION_CARD_CONTACT_IMAGE,
    ACTION_CARD_WEBSITE_IMAGE,
    ACTION_CARDS_GRID_CLASS,
    ActionCardBody,
    ContactPeek,
} from '~/ui-rocco/pages/detail-shared/DetailActionCards';
import {
    ModalPeek,
    useGrowToModal,
} from '~/ui-rocco/pages/detail-shared/ModalPeek';
import { BookMeetingMiniModal } from '~/ui-rocco/pages/detail-shared/BookMeetingPeek';
import { EnquiryMiniModal } from '~/ui-rocco/pages/detail-shared/EnquiryPeek';
import { ARTICLE_GROUND } from '../util/article-ground';
import type { RevampProfessional } from '../api/get-article-revamp-data.server';

const BOOK_A_MEETING = 'Book a Meeting';
const SEND_AN_ENQUIRY = 'Send an enquiry';

interface ArticleHireSectionProps {
    professional: RevampProfessional;
    /** The full professional, for the enquiry modal. */
    enquiryProfessional: EnquiryModalProfessional;
    articleID: number;
    articleTitle: string;
    /** The article's own pictures, grounding the two cards: the banner first. */
    images: string[];
}

/**
 * The project page's two-card offer, at the foot of the article: book a
 * meeting with the practice the article is about, or send them an enquiry —
 * each a photograph with a sentence saying what the reader gets for taking
 * it, and the whole card the control. Each card's hover is the modal it
 * opens, small, and the click grows it into the real one.
 *
 * Book a Meeting opens the one booking gate every entry point opens; with the
 * feature off it falls back to the enquiry modal, as the opening row's button
 * does.
 */
export const ArticleHireSection = ({
    professional,
    enquiryProfessional,
    articleID,
    articleTitle,
    images,
}: ArticleHireSectionProps) => {
    const bookMeetingEnabled = useBookMeetingGateEnabled();
    const openBookMeetingGate = useOpenBookMeetingGate();
    const heroImage = images[0];
    const { openEnquiryModal } = useEnquiryModal({
        professional: enquiryProfessional,
        itemType: 'article',
        siteTreeID: articleID,
        itemTitle: articleTitle,
        itemImage: heroImage,
        brandImage: heroImage,
    });

    const practice = professional.title;
    const meetingCardImage = images[0] ?? ACTION_CARD_WEBSITE_IMAGE;
    const enquiryCardImage =
        images[1] ?? images[0] ?? ACTION_CARD_CONTACT_IMAGE;

    const openGate = () =>
        openBookMeetingGate({
            professionalUrlSegment: professional.urlSegment ?? '',
            siteTreeId: articleID,
            itemTitle: articleTitle,
            professionalName: practice,
            companyLogo: professional.logo,
            logoBackgroundColor: professional.logoBackground,
            paneImage: heroImage,
        });

    const meetingPeekRef = useRef<HTMLDivElement>(null);
    const meetingMini = (
        <BookMeetingMiniModal
            subject={{
                image: heroImage,
                practice,
                companyLogo: professional.logo,
                logoBackgroundColor: professional.logoBackground,
            }}
        />
    );
    const meetingFlight = useGrowToModal(meetingPeekRef, meetingMini);
    const canBook = bookMeetingEnabled && !!professional.urlSegment;
    const onBookMeeting = canBook
        ? () => meetingFlight.grow(openGate)
        : () =>
              openEnquiryModal({
                  title: BOOK_A_MEETING,
                  ctaLabel: BOOK_A_MEETING,
              });

    const enquiryPeekRef = useRef<HTMLDivElement>(null);
    const enquiryMini = (
        <EnquiryMiniModal
            subject={{
                image: heroImage,
                practice,
                note: professional.customEnquiryMessage,
                itemType: 'article',
            }}
        />
    );
    const enquiryFlight = useGrowToModal(enquiryPeekRef, enquiryMini);
    const onSendEnquiry = () =>
        enquiryFlight.grow(() =>
            openEnquiryModal({
                title: SEND_AN_ENQUIRY,
                ctaLabel: SEND_AN_ENQUIRY,
            }),
        );

    return (
        <section
            className={cn(
                'w-full max-md:pt-[3rem] md:pt-[7.5rem]',
                ARTICLE_GROUND,
                DIRECTORY_GUTTER_X,
                // The article's one phone gutter is the prose's 18px, over
                // the directory's 16.
                'px-4.5',
            )}
        >
            <SectionHead
                voice="editorial"
                lead="Like this Article?"
                subject={`Get in touch with ${practice}`}
                subjectLink={professional.link}
            />

            <div className={ACTION_CARDS_GRID_CLASS}>
                <button
                    type="button"
                    onClick={onBookMeeting}
                    className={ACTION_CARD_CLASS}
                >
                    <ActionCardBody
                        image={meetingCardImage}
                        hoverTint
                        peek={
                            canBook ? (
                                <ModalPeek boxRef={meetingPeekRef}>
                                    {meetingMini}
                                </ModalPeek>
                            ) : (
                                <ContactPeek
                                    image={heroImage}
                                    title={practice}
                                    subtitle={articleTitle}
                                />
                            )
                        }
                        copy={
                            <>
                                Book a meeting with {practice} to talk through
                                your own project, their approach and how they
                                could take it on.
                            </>
                        }
                        action={
                            <span
                                className={cn(
                                    detailCta('white-lifted'),
                                    ACTION_CARD_ACTION_CLASS,
                                )}
                            >
                                {BOOK_A_MEETING}
                            </span>
                        }
                    />
                </button>

                <button
                    type="button"
                    onClick={onSendEnquiry}
                    className={ACTION_CARD_CLASS}
                >
                    <ActionCardBody
                        image={enquiryCardImage}
                        hoverTint
                        peek={
                            <ModalPeek boxRef={enquiryPeekRef}>
                                {enquiryMini}
                            </ModalPeek>
                        }
                        copy={
                            <>
                                Send {practice} an enquiry about your project,
                                their availability and fees, or anything you
                                would like to know first.
                            </>
                        }
                        action={
                            <span
                                className={cn(
                                    detailCta('white-lifted'),
                                    ACTION_CARD_ACTION_CLASS,
                                )}
                            >
                                {SEND_AN_ENQUIRY}
                            </span>
                        }
                    />
                </button>
            </div>
            {meetingFlight.element}
            {enquiryFlight.element}
        </section>
    );
};
